/**
 * Whether a saved card has expired or is about to, on the CLUB's calendar.
 *
 * A card marked 11/26 works through the last day of November 2026, so it
 * expires at the end of that day in the club's time zone (settings.timezone),
 * not in UTC and not in the browser's zone. "Expiring" means that last day is
 * at most CARD_EXPIRING_WINDOW_DAYS days from the club's today.
 *
 * ONE rule for every screen that shows a card (PM ba041ca8): the saved-card
 * lists, the billing panel and the dashboard notice. The mobile app is a
 * separate build and keeps its own copy (apps/mobile/src/lib/card-expiry.ts);
 * both run the same case table (cardExpiry.cases.json), and the mobile test
 * fails when the two tables differ.
 */
import {
  addDays,
  calendarDateFromParts,
  calendarDayOf,
  daysBetween,
  type CalendarDate,
} from "@mediatechgroup/calendar-date";
import { clubTimeZone } from "./clubTime";

export type CardExpiryStatus = "expired" | "expiring" | "ok";

/** How many days before the end of the expiry month a card counts as "expiring". */
export const CARD_EXPIRING_WINDOW_DAYS = 30;

/** Stripe sends four-digit years; a two-digit one (26) is read as 2026. */
function fullYear(expYear: number): number {
  return expYear < 100 ? 2000 + expYear : expYear;
}

function toNumber(value: unknown): number | null {
  const n = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof n === "number" && Number.isInteger(n) ? n : null;
}

/** The last calendar day a card marked month/year still works, or null when unreadable. */
export function cardLastValidDay(expMonth: unknown, expYear: unknown): CalendarDate | null {
  const month = toNumber(expMonth);
  const year = toNumber(expYear);
  if (month == null || year == null || month < 1 || month > 12 || year < 0) return null;
  const firstOfNext =
    month === 12 ? calendarDateFromParts(fullYear(year) + 1, 1, 1) : calendarDateFromParts(fullYear(year), month + 1, 1);
  return firstOfNext ? addDays(firstOfNext, -1) : null;
}

function browserZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * "expired", "expiring" (the card stops working within 30 days) or "ok".
 * Null when the month or year cannot be read: unknown is not "expired".
 *
 * `timeZone` defaults to the club's saved zone; without one, the browser's.
 */
export function cardExpiryStatus(
  expMonth: unknown,
  expYear: unknown,
  options: { now?: Date; timeZone?: string | null } = {}
): CardExpiryStatus | null {
  const lastDay = cardLastValidDay(expMonth, expYear);
  if (!lastDay) return null;
  const zone = options.timeZone === undefined ? clubTimeZone() : options.timeZone;
  const fallbackZone = browserZone();
  const today = calendarDayOf(options.now ?? new Date(), zone, 0, fallbackZone ? { fallbackZone } : undefined);
  if (!today) return null;
  const daysLeft = daysBetween(today, lastDay);
  if (daysLeft < 0) return "expired";
  if (daysLeft <= CARD_EXPIRING_WINDOW_DAYS) return "expiring";
  return "ok";
}

/** "11/26": the way the card itself prints it. Empty when unreadable. */
export function formatCardExpiry(expMonth: unknown, expYear: unknown): string {
  const month = toNumber(expMonth);
  const year = toNumber(expYear);
  if (month == null || year == null || month < 1 || month > 12) return "";
  return `${String(month).padStart(2, "0")}/${String(fullYear(year) % 100).padStart(2, "0")}`;
}

/** The words on the saved-card badge, or null when no badge is shown. */
export function cardExpiryBadgeLabel(status: CardExpiryStatus | null): string | null {
  if (status === "expired") return "Expired";
  if (status === "expiring") return "Expires soon";
  return null;
}

/**
 * Tailwind classes for the badge. Status colors (amber / red), never the
 * club's brand color: a warning must not take on whatever color a club picked.
 */
export function cardExpiryBadgeClasses(status: CardExpiryStatus | null): string {
  if (status === "expired") return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
  return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
}
